import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  ButtonGroup,
} from "@mui/material";
import variables from "../../../assets/styles/variables.module.scss";
import AddIcon from "@mui/icons-material/Add";
import WorkflowCard from "../../../Shared-ui/SharedComponents/Cards/WorkflowCard";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Workflow } from "../../../Shared-ui/Types/Workflow";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import SearchIcon from "@mui/icons-material/Search";
import Confirm from "../../../Shared-ui/Types/Confirm";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { WORKFLOW_NODES } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import WorkflowService, { WorkflowListItem } from "../../../Services/WorkflowService";
import Workflow_emptystateimage from "../../../assets/images/Workflows_emptystateimage.svg";
import { useSelector } from "react-redux";
import { START_LOADING, STOP_LOADING } from "../../../Shared-ui/Store/LoadingReducer/LoaderTypes";

// Styled Button
const StyledColorButton = styled(Button)(({ theme }) => ({
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
  textTransform: "capitalize",
  fontWeight: 400,
  borderRadius: "8px",
  height: "40px",
  whiteSpace: "nowrap",
}));

const WorkflowsData = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [workflowsAll, setWorkflowsAll] = useState<WorkflowListItem[]>([]);
  const [searchText, setSearchText] = useState<string>(""); // Input field value
  const [searchQuery, setSearchQuery] = useState<string>(""); // Actual search query sent to API
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const prevPage = useRef<number>(page);
  const pageLimitRef = useRef(8);
  const [totalWorkflows, setTotalWorkflows] = useState<number>(0);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterType, setFilterType] = useState<string>("All Workflows");
  const [filteredWorkflows, setFilteredWorkflows] = useState<WorkflowListItem[]>([]);
  const loaderStatus = useSelector((state: any) => state.LoaderReducer);

  // Avoid duplicate API calls (e.g., due to StrictMode double-invoke or overlapping effects)
  const lastFetchKeyRef = useRef<string>("");

  // Fetch workflows on page or search query change (server-side pagination)
  useEffect(() => {
    const key = `${page}-${searchQuery}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;

    getWorkflows();
  }, [page, searchQuery]);

  const getWorkflows = async () => {
    try {
      dispatch(StartLoading("Workflow"));
      const response = await WorkflowService.getWorkflows(page, limit, searchQuery || undefined);
      // Handle paginated response with items and pagination
      setWorkflows(response.items || []);
      setWorkflowsAll(response.items || []);
      setTotalWorkflows(response.pagination?.total || 0);
      dispatch(StopLoading("Workflow"));
    } catch (error) {
      dispatch(StopLoading("Workflow"));
      dispatch(ShowError("Failed to fetch workflows"));
      // Set empty arrays on error to prevent undefined state
      setWorkflows([]);
      setWorkflowsAll([]);
      setTotalWorkflows(0);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);
    // Automatically fetch all workflows when search box is cleared
    if (value === '') {
      setSearchQuery('');
      setPage(1);
    }
  };

  const handleSearchClick = () => {
    setSearchQuery(searchText);
    setPage(1); // Reset to first page when search is triggered
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleCreateWorkflowClick = () => {
    navigate('/workflows/create');
  };

  const editinWorkflow = (workflow: WorkflowListItem) => {
    navigate(`/workflows/edit/${workflow.id}`);
  };

  const deleteWorkflow = (workflow: WorkflowListItem) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Delete Workflow",
      info: `Are you sure you want to delete "${workflow.name}"?`,
      functionName: async () => {
        try {
          await WorkflowService.deleteWorkflow(workflow.id);
          //FIX pagination after delete
          const newTotal = Math.max(0, totalWorkflows - 1);
          const newMaxPage = Math.max(1, Math.ceil(newTotal / limit));
          const nextPage = Math.min(page, newMaxPage);

          setTotalWorkflows(newTotal);

          if (nextPage !== page) {
            setPage(nextPage); // useEffect will auto call getWorkflows
          } else {
            getWorkflows();
          }
          setConfirmPopupData({ show: false });
        } catch (error) {
          dispatch(ShowError("Failed to delete workflow"));
        }
      },
    });
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search workflows..."
          value={searchText}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyPress}
          size={isSmallScreen ? "small" : "medium"}
          sx={{
            minWidth: { xs: "100%", sm: "300px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSearchClick}
                  edge="end"
                  aria-label="search"
                  sx={{ color: variables.primaryButton }}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <ButtonGroup
          variant="contained"
          sx={{
            borderRadius: { sm: "8px", xs: "4px" },
            overflow: "hidden",
            boxShadow: "none",
            "& .MuiButton-root": {
              color: variables.whiteColor,
              textTransform: "capitalize",
              fontWeight: "400",
              whiteSpace: "nowrap",
              backgroundColor: variables.primaryButton,
              "&:hover": {
                backgroundColor: variables.primaryColor,
              },
            },
          }}
          size={isSmallScreen ? "small" : "large"}
        >
          <StyledColorButton
            startIcon={<AddIcon />}
            onClick={handleCreateWorkflowClick}
            sx={{
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 }
              },
            }}
          >
            {!isSmallScreen && "Create Workflow"}
          </StyledColorButton>
        </ButtonGroup>
      </Box>

      {/* Workflow Cards Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr", lg: "1fr 1fr 1fr 1fr" }, gap: 2 }}>
        {workflows.length > 0 ? (
          workflows.map((workflow, index) => (
            <WorkflowCard
              key={workflow.id || index}
              workflow={workflow}
              deleteFun={deleteWorkflow}
              editFun={editinWorkflow}
            />
          ))
        ) : (
          <Box sx={{ gridColumn: "1/-1", textAlign: "center", py: 6 }}>
            <img
              src={Workflow_emptystateimage}
              alt="No workflows"
              style={{ width: "200px", marginBottom: "16px" }}
            />
            <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
              No workflows found
            </Typography>
            <Typography variant="body2" sx={{ color: "#888", mb: 2 }}>
              Click the button below to create your first workflow.
            </Typography>
            <StyledColorButton startIcon={<AddIcon />} onClick={handleCreateWorkflowClick}>
              Create Workflow
            </StyledColorButton>
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {totalWorkflows > limit && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(totalWorkflows / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Confirm Delete Popup */}
      <ConfirmPopup
        details={confirmPopupData}
      />
    </Box>
  );
};

export default WorkflowsData;
